import { useState, useEffect } from 'react'
import { extractUrls } from '@/lib/utils/urls'

interface PreviewData {
  url: string
  title: string | null
  description: string | null
  image: string | null
  siteName: string | null
}

const previewCache = new Map<string, PreviewData | null>()

async function fetchPreview(url: string): Promise<PreviewData | null> {
  const cached = previewCache.get(url)
  if (cached !== undefined) return cached

  try {
    const res = await fetch(`/api/link-preview?url=${encodeURIComponent(url)}`)
    if (!res.ok) throw new Error()
    const data = await res.json() as PreviewData
    if (!data.title && !data.description && !data.image) {
      previewCache.set(url, null)
      return null
    }
    previewCache.set(url, data)
    return data
  } catch {
    previewCache.set(url, null)
    return null
  }
}

interface LinkPreviewProps {
  text: string
}

export function LinkPreview({ text }: LinkPreviewProps) {
  const [preview, setPreview] = useState<PreviewData | null | 'loading'>(() => {
    const urls = extractUrls(text)
    if (!urls[0]) return null
    const cached = previewCache.get(urls[0])
    return cached !== undefined ? cached : 'loading'
  })
  const urls = extractUrls(text)
  const firstUrl = urls[0]

  useEffect(() => {
    if (!firstUrl) return
    const cached = previewCache.get(firstUrl)
    if (cached !== undefined) {
      setPreview(cached)
      return
    }
    setPreview('loading')
    fetchPreview(firstUrl).then(setPreview)
  }, [firstUrl])

  if (!firstUrl || preview === null) return null

  if (preview === 'loading') {
    return (
      <div className="mt-2 max-w-sm border border-[rgb(var(--np-border))] rounded-lg overflow-hidden bg-[rgb(var(--np-raised)/0.3)] h-24 flex items-center justify-center">
        <span className="text-xs text-[rgb(var(--np-tx-muted))]">Loading preview...</span>
      </div>
    )
  }

  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block mt-2 max-w-sm border border-[rgb(var(--np-border))] rounded-lg overflow-hidden hover:border-[rgb(var(--np-border-bright))] transition-colors bg-[rgb(var(--np-raised)/0.5)]"
    >
      {preview.image && (
        <img
          src={`/api/image-proxy?url=${encodeURIComponent(preview.image)}`}
          alt=""
          className="w-full h-40 object-cover"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
        />
      )}
      <div className="px-3 py-2.5">
        {preview.siteName && (
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[rgb(var(--np-accent))] mb-0.5">
            {preview.siteName}
          </p>
        )}
        {preview.title && (
          <p className="text-sm font-medium text-[rgb(var(--np-tx-primary))] line-clamp-2">
            {preview.title}
          </p>
        )}
        {preview.description && (
          <p className="text-xs text-[rgb(var(--np-tx-muted))] line-clamp-2 mt-0.5">
            {preview.description}
          </p>
        )}
      </div>
    </a>
  )
}
